<template>
  <div>
    <!-- Dashboard metrics - Using standard B-Card components -->
    <b-row class="match-height mb-2">
      <b-col
        xl="3"
        md="6"
        sm="12"
      >
        <b-card class="text-center bg-light-primary">
          <div class="mb-1">
            <feather-icon
              icon="TruckIcon"
              size="21"
              class="text-primary"
            />
          </div>
          <h2 class="font-weight-bolder">
            {{ totalCars || 0 }}
          </h2>
          <p class="card-text">
            Total des voitures
          </p>
        </b-card>
      </b-col>
      <b-col
        xl="3"
        md="6"
        sm="12"
      >
        <b-card class="text-center bg-light-success">
          <div class="mb-1">
            <feather-icon
              icon="CheckCircleIcon"
              size="21"
              class="text-success"
            />
          </div>
          <h2 class="font-weight-bolder">
            {{ activeCount }}
          </h2>
          <p class="card-text">
            Voitures actives
          </p>
        </b-card>
      </b-col>
      <b-col
        xl="3"
        md="6"
        sm="12"
      >
        <b-card class="text-center bg-light-warning">
          <div class="mb-1">
            <feather-icon
              icon="UserIcon"
              size="21"
              class="text-warning"
            />
          </div>
          <h2 class="font-weight-bolder">
            {{ assignedCount }}
          </h2>
          <p class="card-text">
            Avec conducteurs
          </p>
        </b-card>
      </b-col>
      <b-col
        xl="3"
        md="6"
        sm="12"
      >
        <b-card class="text-center bg-light-info">
          <div class="mb-1">
            <feather-icon
              icon="CalendarIcon"
              size="21"
              class="text-info"
            />
          </div>
          <h2 class="font-weight-bolder">
            {{ averageYear }}
          </h2>
          <p class="card-text">
            Année moyenne
          </p>
        </b-card>
      </b-col>
    </b-row>

    <!-- Advanced filters -->
    <b-card class="mb-2">
      <b-card-title>Filtres avancés</b-card-title>
      <b-row>
        <b-col
          md="6"
          sm="12"
        >
          <b-form-group label="Rechercher">
            <b-form-input
              v-model="filters.search"
              v-debounce:500ms="getCars"
              placeholder="Rechercher par matricule, marque..."
              class="filter-input"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="6"
          sm="12"
        >
          <b-form-group label="État">
            <v-select
              v-model="filters.active"
              :options="statusOptions"
              placeholder="Tous"
              :clearable="true"
              label="label"
              class="select-search-option"
              :reduce="option => option.value"
              @input="getCars"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Additional filters (toggleable) -->
      <div v-if="showMoreFilters">
        <b-row>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group label="Marque">
              <b-form-input
                v-model="filters.brand"
                v-debounce:500ms="getCars"
                placeholder="Filtrer par marque"
                class="filter-input"
              />
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group label="Couleur">
              <b-form-input
                v-model="filters.color"
                v-debounce:500ms="getCars"
                placeholder="Filtrer par couleur"
                class="filter-input"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group label="Année">
              <div class="d-flex align-items-center">
                <b-form-input
                  v-model="filters.yearMin"
                  v-debounce:500ms="getCars"
                  placeholder="Min"
                  type="number"
                  min="1900"
                  max="2100"
                  class="mr-2 filter-input"
                />
                <span class="mx-1">à</span>
                <b-form-input
                  v-model="filters.yearMax"
                  v-debounce:500ms="getCars"
                  placeholder="Max"
                  type="number"
                  min="1900"
                  max="2100"
                  class="ml-2 filter-input"
                />
              </div>
            </b-form-group>
          </b-col>

          <b-col
            md="6"
            sm="12"
          >
            <b-form-group label="Conducteur assigné">
              <v-select
                v-model="filters.hasDriver"
                :options="driverOptions"
                placeholder="Tous"
                :clearable="true"
                label="label"
                class="select-search-option"
                :reduce="option => option.value"
                @input="getCars"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Advanced filters section -->
        <b-row>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group label="Statut technique">
              <v-select
                v-model="filters.status"
                :options="carStatusOptions"
                placeholder="Tous les statuts"
                :clearable="true"
                label="label"
                class="select-search-option"
                :reduce="option => option.value"
                @input="getCars"
              />
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group label="Date d'immatriculation">
              <b-form-datepicker
                v-model="filters.registrationDate"
                placeholder="Sélectionner une date"
                :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                class="mb-2 filter-input"
                @input="getCars"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group label="Type de carburant">
              <v-select
                v-model="filters.fuelType"
                :options="fuelTypeOptions"
                placeholder="Tous les types"
                :clearable="true"
                label="label"
                class="select-search-option"
                :reduce="option => option.value"
                @input="getCars"
              />
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group label="Transmission">
              <v-select
                v-model="filters.transmission"
                :options="transmissionOptions"
                placeholder="Toutes les transmissions"
                :clearable="true"
                label="label"
                class="select-search-option"
                :reduce="option => option.value"
                @input="getCars"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group label="Capacité (m³)">
              <div class="d-flex align-items-center">
                <b-form-input
                  v-model="filters.capacityMin"
                  v-debounce:500ms="getCars"
                  placeholder="Min"
                  type="number"
                  step="0.1"
                  min="0"
                  class="mr-2 filter-input"
                />
                <span class="mx-1">à</span>
                <b-form-input
                  v-model="filters.capacityMax"
                  v-debounce:500ms="getCars"
                  placeholder="Max"
                  type="number"
                  step="0.1"
                  min="0"
                  class="ml-2 filter-input"
                />
              </div>
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group label="Kilométrage">
              <div class="d-flex align-items-center">
                <b-form-input
                  v-model="filters.mileageMin"
                  v-debounce:500ms="getCars"
                  placeholder="Min"
                  type="number"
                  min="0"
                  class="mr-2 filter-input"
                />
                <span class="mx-1">à</span>
                <b-form-input
                  v-model="filters.mileageMax"
                  v-debounce:500ms="getCars"
                  placeholder="Max"
                  type="number"
                  min="0"
                  class="ml-2 filter-input"
                />
              </div>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group label="Utilisation dans les feuilles de route">
              <v-select
                v-model="filters.inUseInRoadmap"
                :options="roadmapUsageOptions"
                placeholder="Tous"
                :clearable="true"
                label="label"
                class="select-search-option"
                :reduce="option => option.value"
                @input="getCars"
              />
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group label="Entretien prévu">
              <v-select
                v-model="filters.maintenanceDue"
                :options="maintenanceOptions"
                placeholder="Tous"
                :clearable="true"
                label="label"
                class="select-search-option"
                :reduce="option => option.value"
                @input="getCars"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group label="Expiration d'assurance">
              <b-form-datepicker
                v-model="filters.insuranceExpiry"
                placeholder="Sélectionner une date"
                :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                class="mb-2 filter-input"
                @input="getCars"
              />
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group label="Expiration contrôle technique">
              <b-form-datepicker
                v-model="filters.technicalInspectionExpiry"
                placeholder="Sélectionner une date"
                :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                class="mb-2 filter-input"
                @input="getCars"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="12">
            <b-form-group label="Nom du conducteur">
              <b-form-input
                v-model="filters.driverName"
                v-debounce:500ms="getCars"
                placeholder="Rechercher par nom du conducteur"
                class="filter-input"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <div class="d-flex justify-content-between mt-1">
        <div>
          <b-button
            variant="outline-secondary"
            size="sm"
            @click="resetFilters"
          >
            <span>Réinitialiser</span>
          </b-button>
        </div>
        <b-button
          variant="outline-primary"
          size="sm"
          @click="toggleMoreFilters"
        >
          <span>{{ showMoreFilters ? 'Moins de filtres' : 'Plus de filtres' }}</span>
        </b-button>
      </div>
    </b-card>

    <!-- Header section with title and badge -->
    <div class="custom-search d-flex justify-content-between mb-2">
      <div class="d-flex align-items-center">
        <h3 class="mb-0 mr-2">
          Liste des Voitures
        </h3>
        <b-badge
          variant="danger"
          pill
          class="badge-round"
        >
          {{ totalCars || 0 }}
        </b-badge>
      </div>
      <div class="d-flex">
        <b-form-group
          v-if="user.role==='admin'"
          class="mb-0"
        >
          <b-button
            variant="outline-primary"
            class="mr-1"
            @click="$refs.quickCreateCarModal.show()"
          >
            <feather-icon icon="ZapIcon" />
            <span class="ml-1">Création Rapide</span>
          </b-button>
          <b-button
            :to="{ name: 'car-add' }"
            variant="primary"
            class="d-inline-block"
          >
            <feather-icon icon="PlusIcon" />
            <span class="ml-1">Ajouter une voiture</span>
          </b-button>
        </b-form-group>
      </div>
    </div>

    <!-- Loading states with skeletons -->
    <div
      v-if="isLoading"
      class="mb-2"
    >
      <b-card>
        <b-skeleton-table
          :rows="5"
          :columns="6"
          :table-props="{ bordered: true, striped: true }"
        />
      </b-card>
    </div>

    <!-- Enhanced table -->
    <vue-good-table
      v-else
      ref="carsTable"
      :columns="columns"
      :rows="cars"
      :search-options="{
        enabled: true,
        externalQuery: searchTerm }"
      :pagination-options="{
        enabled: true,
        perPage: pageLength,
        mode: 'records'
      }"
      :total-rows="totalCars"
      :is-loading="isLoading"
      style-class="vgt-table bordered striped hover responsive"
      table-class="cars-table"
      :select-options="{
        enabled: user.role === 'admin',
        selectOnCheckboxOnly: true
      }"
      @on-page-change="onPageChange"
      @on-per-page-change="onPerPageChange"
      @on-selected-rows-change="onSelectedRowsChange"
    >
      <template
        slot="table-row"
        slot-scope="props"
      >
        <!-- Enhanced serial_number column with link -->
        <span v-if="props.column.field === 'serial_number'">
          <div class="d-flex align-items-center">
            <router-link
              :to="{ name: 'car-show', params: { id: props.row.id }}"
              class="font-weight-bold text-primary"
            >
              {{ props.row.serial_number }}
            </router-link>
            <b-badge
              v-if="props.row.driver"
              variant="light-info"
              class="ml-1"
            >
              Avec conducteur
            </b-badge>
          </div>
        </span>

        <!-- Status column: show only badge, no enable/disable action -->
        <span v-else-if="props.column.field === 'is_active'">
          <div class="status-cell">
            <transition
              name="fade"
              mode="out-in"
            >
              <div
                v-if="props.row.is_active === true"
                key="active"
                class="d-flex flex-column align-items-center"
              >
                <b-badge
                  class="badge-glow bg-success mb-1 status-badge"
                  pill
                >
                  <feather-icon
                    icon="CheckIcon"
                    class="mr-25"
                    size="12"
                  />
                  <span>Activé</span>
                </b-badge>
              </div>
              <div
                v-else
                key="inactive"
                class="d-flex flex-column align-items-center"
              >
                <b-badge
                  class="badge-glow bg-danger mb-1 status-badge"
                  pill
                >
                  <feather-icon
                    icon="XIcon"
                    class="mr-25"
                    size="12"
                  />
                  <span>Désactivé</span>
                </b-badge>
              </div>
            </transition>
          </div>
        </span>

        <!-- Driver column -->
        <span v-else-if="props.column.field === 'driver'">
          <div v-if="props.row.driver">
            {{ props.row.driver.first_name }} {{ props.row.driver.last_name }}
          </div>
          <div
            v-else
            class="text-muted"
          >
            Non assigné
          </div>
        </span>

        <!-- Enhanced action column: add enable/disable action here -->
        <span v-else-if="props.column.field === 'action'">
          <div class="d-flex align-items-center justify-content-center action-icons-gap">
            <router-link
              v-b-tooltip.hover
              class="text-info mr-1"
              :to="{ name: 'car-show', params: { id: props.row.id }}"
              title="Voir détails"
            >
              <feather-icon
                icon="EyeIcon"
                class="cursor-pointer"
                size="16"
              />
            </router-link>

            <router-link
              v-if="user.role === 'admin'"
              v-b-tooltip.hover
              class="text-warning mr-1"
              :to="{ name: 'car-edit', params: { id: props.row.id }}"
              title="Modifier"
            >
              <feather-icon
                icon="EditIcon"
                class="cursor-pointer"
                size="16"
              />
            </router-link>

            <feather-icon
              v-if="user.role === 'admin'"
              v-b-tooltip.hover
              icon="TrashIcon"
              class="cursor-pointer text-danger mr-1"
              size="16"
              title="Supprimer"
              @click="confirmDelete(props.row.id)"
            />

            <!-- Enable/disable action as icon for consistency -->
            <span
              v-if="user.role === 'admin' && props.row.is_active === true"
              class="d-flex align-items-center"
            >
              <feather-icon
                v-b-tooltip.hover
                icon="XCircleIcon"
                class="cursor-pointer text-danger ml-1"
                size="18"
                title="Désactiver cette voiture"
                :style="toggleStatusLoading === props.row.id ? 'opacity:0.5;pointer-events:none;' : ''"
                @click="confirmToggleStatus(props.row.id, false)"
              />
              <b-spinner
                v-if="toggleStatusLoading === props.row.id"
                small
                type="grow"
                class="ml-1 align-middle"
              />
            </span>
            <span
              v-if="user.role === 'admin' && props.row.is_active === false"
              class="d-flex align-items-center"
            >
              <feather-icon
                v-b-tooltip.hover
                icon="CheckCircleIcon"
                class="cursor-pointer text-success ml-1"
                size="18"
                title="Activer cette voiture"
                :style="toggleStatusLoading === props.row.id ? 'opacity:0.5;pointer-events:none;' : ''"
                @click="confirmToggleStatus(props.row.id, true)"
              />
              <b-spinner
                v-if="toggleStatusLoading === props.row.id"
                small
                type="grow"
                class="ml-1 align-middle"
              />
            </span>
          </div>
        </span>

        <!-- Column: Common -->
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <!-- Empty state template -->
      <template slot="emptystate">
        <div class="empty-state text-center py-5">
          <feather-icon
            icon="TruckIcon"
            size="48"
            class="text-muted mb-2"
          />
          <h4>Aucune voiture trouvée</h4>
          <p class="text-muted">
            {{ searchTerm ? 'Aucune voiture correspondant à votre recherche.' : 'Aucune voiture disponible.' }}
          </p>
          <div v-if="hasActiveFilters">
            <p>Essayez de modifier vos filtres</p>
            <b-button
              variant="outline-primary"
              size="sm"
              @click="resetFilters"
            >
              Réinitialiser les filtres
            </b-button>
          </div>
        </div>
      </template>

      <!-- Add bulk actions for selected rows -->
      <template slot="selected-row-actions">
        <div class="selected-actions p-2 bg-light-primary rounded d-flex align-items-center">
          <span class="mr-2">{{ selectedRows.length }} sélectionné(s)</span>
          <b-button
            v-if="user.role === 'admin' && selectedRows.length > 0"
            variant="outline-success"
            size="sm"
            class="mr-1"
            @click="bulkActivate"
          >
            <feather-icon
              icon="CheckCircleIcon"
              size="14"
              class="mr-1"
            />
            Activer
          </b-button>
          <b-button
            v-if="user.role === 'admin' && selectedRows.length > 0"
            variant="outline-danger"
            size="sm"
            @click="bulkDeactivate"
          >
            <feather-icon
              icon="XCircleIcon"
              size="14"
              class="mr-1"
            />
            Désactiver
          </b-button>
          <b-button
            variant="link"
            size="sm"
            class="ml-2"
            @click="clearSelection"
          >
            Annuler
          </b-button>
        </div>
      </template>

      <!-- pagination -->
      <template
        slot="pagination-bottom"
        slot-scope="props"
      >
        <div class="d-flex justify-content-between flex-wrap">
          <div class="d-flex align-items-center mb-0 mt-1">
            <span class="text-nowrap">
              Affichage
            </span>
            <b-form-select
              v-model="pageLength"
              :options="['50','100','500']"
              class="mx-1"
              @input="(value)=>props.perPageChanged({currentPerPage:value})"
            />
            <span class="text-nowrap"> de {{ props.total }} résultat{{ props.total > 1 ? 's' : '' }} trouvé{{ props.total > 1 ? 's' : '' }} </span>
          </div>
          <div>
            <b-pagination
              :value="1"
              :total-rows="props.total"
              :per-page="pageLength"
              first-number
              last-number
              align="right"
              prev-class="prev-item"
              next-class="next-item"
              class="mt-1 mb-0"
              @input="(value)=>props.pageChanged({currentPage:value})"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </template>
    </vue-good-table>

    <!-- Status confirmation modal -->
    <b-modal
      id="confirm-status-modal"
      ref="confirmStatusModal"
      :title="statusModalTitle"
      ok-variant="primary"
      cancel-variant="outline-secondary"
      @ok="handleStatusConfirmed"
      @cancel="cancelStatusChange"
    >
      <p>{{ statusModalMessage }}</p>
    </b-modal>

    <!-- Delete confirmation modal -->
    <b-modal
      id="confirm-delete-modal"
      ref="confirmDeleteModal"
      title="Confirmer la suppression"
      ok-variant="danger"
      ok-title="Supprimer"
      cancel-variant="outline-secondary"
      @ok="handleDeleteConfirmed"
      @cancel="cancelDelete"
    >
      <p>Êtes-vous sûr de vouloir supprimer cette voiture ? Cette action est irréversible.</p>
    </b-modal>

    <!-- Bulk action confirmation modal -->
    <b-modal
      id="confirm-bulk-modal"
      ref="confirmBulkModal"
      :title="bulkModalTitle"
      ok-variant="primary"
      cancel-variant="outline-secondary"
      @ok="handleBulkConfirmed"
      @cancel="cancelBulkChange"
    >
      <p>{{ bulkModalMessage }}</p>
      <ul v-if="selectedRows.length <= 5">
        <li
          v-for="row in selectedRows"
          :key="row.id"
        >
          {{ row.serial_number }} ({{ row.brand }})
        </li>
      </ul>
      <p v-else>
        {{ selectedRows.length }} voitures seront affectées.
      </p>
    </b-modal>

    <!-- Quick create car modal -->
    <quick-create-car-modal
      ref="quickCreateCarModal"
      @car-created="getCars"
    />

    <div
      id="cars-table-help"
      class="sr-only"
    >
      Ce tableau présente la liste des véhicules du parc logistique. Utilisez les filtres et la recherche pour affiner les résultats.
    </div>
  </div>
</template>

<script>
import {
  BPagination,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BButton,
  BSpinner,
  BCard,
  BCardTitle,
  BRow,
  BCol,
  BBadge,
  BSkeletonTable,
  BModal,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import vSelect from 'vue-select'
import storeAuth from '@/store/store'
import QuickCreateCarModal from '@/components/modals/QuickCreateCarModal.vue'

export default {
  components: {
    VueGoodTable,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BButton,
    BSpinner,
    BCard,
    BCardTitle,
    BRow,
    BCol,
    BBadge,
    BSkeletonTable,
    BModal,
    vSelect,
    QuickCreateCarModal,
  },
  data() {
    return {
      isLoading: false,
      pageLength: 100,
      user: {},
      columns: [
        {
          label: 'Matricule',
          field: 'serial_number',
          filterOptions: {
            enabled: true,
            placeholder: 'chercher matricule',
          },
          tdClass: 'font-weight-bold',
        },
        {
          label: 'Marque',
          field: 'brand',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher marque',
          },
        },
        {
          label: 'Modèle',
          field: 'model',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher modèle',
          },
        },
        {
          label: 'Année',
          field: 'year',
          type: 'number',
          filterOptions: {
            enabled: true,
          },
          tdClass: 'text-center',
        },
        {
          label: 'Couleur',
          field: 'color',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher couleur',
          },
        },
        {
          label: 'Conducteur',
          field: row => (row.default_driver_details ? row.default_driver_details.username : 'Non assigné'),
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher conducteur',
          },
          thClass: 'min-width-200',
          tdClass: 'min-width-200',
          sortable: true,
        },
        {
          label: 'État',
          field: 'is_active',
          filterOptions: {
            enabled: true,
          },
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          label: 'Action',
          field: 'action',
          thClass: 'text-center',
          tdClass: 'text-center',
        },
      ],
      cars: [],
      searchTerm: '',
      showMoreFilters: false,
      totalRecords: 0,
      currentPage: 1,
      toggleStatusLoading: null,
      carToToggle: null,
      newStatus: null,
      statusModalTitle: '',
      statusModalMessage: '',
      carToDelete: null,
      selectedRows: [],
      bulkModalTitle: '',
      bulkModalMessage: '',
      bulkAction: null,

      // Filters
      filters: {
        search: '',
        active: null,
        brand: '',
        color: '',
        yearMin: null,
        yearMax: null,
        hasDriver: null,
        status: null,
        fuelType: null,
        transmission: null,
        capacityMin: null,
        capacityMax: null,
        mileageMin: null,
        mileageMax: null,
        registrationDate: null,
        insuranceExpiry: null,
        technicalInspectionExpiry: null,
        inUseInRoadmap: null,
        maintenanceDue: null,
        driverName: '',
      },

      // Filter options
      statusOptions: [
        { value: true, label: 'Actif' },
        { value: false, label: 'Inactif' },
      ],
      driverOptions: [
        { value: true, label: 'Assigné' },
        { value: false, label: 'Non assigné' },
      ],
      carStatusOptions: [
        { value: 'available', label: 'Disponible' },
        { value: 'in_use', label: 'En utilisation' },
        { value: 'maintenance', label: 'En maintenance' },
        { value: 'out_of_service', label: 'Hors service' },
      ],
      fuelTypeOptions: [
        { value: 'Gasoline', label: 'Essence' },
        { value: 'Diesel', label: 'Diesel' },
        { value: 'Electric', label: 'Électrique' },
        { value: 'Hybrid', label: 'Hybride' },
        { value: 'CNG', label: 'Gaz naturel' },
        { value: 'LPG', label: 'GPL' },
      ],
      transmissionOptions: [
        { value: 'Automatic', label: 'Automatique' },
        { value: 'Manual', label: 'Manuelle' },
        { value: 'Semi-automatic', label: 'Semi-automatique' },
        { value: 'CVT', label: 'CVT' },
      ],
      roadmapUsageOptions: [
        { value: true, label: 'Utilisé dans une feuille de route' },
        { value: false, label: 'Non utilisé dans une feuille de route' },
      ],
      maintenanceOptions: [
        { value: true, label: 'Maintenance prévue ou expiration imminente' },
        { value: false, label: 'Aucune maintenance prévue prochainement' },
      ],
    }
  },
  computed: {
    // Add the missing totalCars computed property
    totalCars() {
      return this.cars.length
    },

    activeCount() {
      return this.cars.filter(car => car.is_active).length
    },

    inactiveCount() {
      return this.cars.filter(car => !car.is_active).length
    },

    assignedCount() {
      return this.cars.filter(car => car.default_driver !== null).length
    },

    unassignedCount() {
      return this.cars.filter(car => car.default_driver === null).length
    },

    averageYear() {
      const carsWithYear = this.cars.filter(car => car.year)
      if (carsWithYear.length === 0) return 'N/A'

      const sum = carsWithYear.reduce((acc, car) => acc + car.year, 0)
      return Math.round(sum / carsWithYear.length)
    },
    hasActiveFilters() {
      return (
        this.filters.search !== ''
        || this.filters.active !== null
        || this.filters.brand !== ''
        || this.filters.color !== ''
        || this.filters.yearMin !== null
        || this.filters.yearMax !== null
        || this.filters.hasDriver !== null
        || this.filters.status !== null
        || this.filters.fuelType !== null
        || this.filters.transmission !== null
        || this.filters.capacityMin !== null
        || this.filters.capacityMax !== null
        || this.filters.mileageMin !== null
        || this.filters.mileageMax !== null
        || this.filters.registrationDate !== null
        || this.filters.insuranceExpiry !== null
        || this.filters.technicalInspectionExpiry !== null
        || this.filters.inUseInRoadmap !== null
        || this.filters.maintenanceDue !== null
        || this.filters.driverName !== ''
      )
    },
  },
  created() {
    this.user = storeAuth.state.user
    this.getCars()
  },
  methods: {
    // Toggle more filters
    toggleMoreFilters() {
      this.showMoreFilters = !this.showMoreFilters
    },

    // Get cars with applied filters
    async getCars() {
      this.isLoading = true
      try {
        let url = '/api/cars/'

        // Add query params for filters
        const params = new URLSearchParams()

        if (this.filters.search) {
          params.append('serial_number__icontains', this.filters.search)
        }

        if (this.filters.active !== null) {
          params.append('is_active', this.filters.active)
        }

        if (this.filters.brand) {
          params.append('brand__icontains', this.filters.brand)
        }

        if (this.filters.color) {
          params.append('color__icontains', this.filters.color)
        }

        if (this.filters.yearMin) {
          params.append('year__gte', this.filters.yearMin)
        }

        if (this.filters.yearMax) {
          params.append('year__lte', this.filters.yearMax)
        }

        if (this.filters.hasDriver !== null) {
          params.append('has_driver', this.filters.hasDriver)
        }

        if (this.filters.status) {
          params.append('status', this.filters.status)
        }

        if (this.filters.fuelType) {
          params.append('fuel_type', this.filters.fuelType)
        }

        if (this.filters.transmission) {
          params.append('transmission', this.filters.transmission)
        }

        if (this.filters.capacityMin) {
          params.append('capacity__gte', this.filters.capacityMin)
        }

        if (this.filters.capacityMax) {
          params.append('capacity__lte', this.filters.capacityMax)
        }

        if (this.filters.mileageMin) {
          params.append('mileage__gt', this.filters.mileageMin)
        }

        if (this.filters.mileageMax) {
          params.append('mileage__lt', this.filters.mileageMax)
        }

        if (this.filters.registrationDate) {
          params.append('registration_date', this.formatDate(this.filters.registrationDate))
        }

        if (this.filters.insuranceExpiry) {
          params.append('insurance_expiry__gte', this.formatDate(this.filters.insuranceExpiry))
        }

        if (this.filters.technicalInspectionExpiry) {
          params.append('technical_inspection_expiry__gte', this.formatDate(this.filters.technicalInspectionExpiry))
        }

        if (this.filters.inUseInRoadmap !== null) {
          params.append('in_use_in_roadmap', this.filters.inUseInRoadmap)
        }

        if (this.filters.maintenanceDue !== null) {
          params.append('maintenance_due', this.filters.maintenanceDue)
        }

        if (this.filters.driverName) {
          params.append('driver_name', this.filters.driverName)
        }

        // Pagination params
        params.append('limit', this.pageLength)
        params.append('offset', (this.currentPage - 1) * this.pageLength)

        // Append params to URL if any exist
        if (params.toString()) url = `${url}?${params.toString()}`

        const { data } = await axios.get(url)

        // Handle paginated response from backend if implemented
        if (data.results) {
          this.cars = data.results
          this.totalRecords = data.count
        } else {
          this.cars = data
          this.totalRecords = data.length
        }
      } catch (error) {
        console.error('Error fetching cars:', error)
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des voitures')
      } finally {
        this.isLoading = false
      }
    },

    // Format date for API requests
    formatDate(date) {
      if (!date) return null

      // Convert to YYYY-MM-DD format
      if (typeof date === 'string' && date.includes('-')) {
        return date // Already in correct format
      }

      const d = new Date(date)
      let month = `${d.getMonth() + 1}`
      let day = `${d.getDate()}`
      const year = d.getFullYear()

      if (month.length < 2) month = `0${month}`
      if (day.length < 2) day = `0${day}`

      return [year, month, day].join('-')
    },

    // Reset filters
    resetFilters() {
      this.filters = {
        search: '',
        active: null,
        brand: '',
        color: '',
        yearMin: null,
        yearMax: null,
        hasDriver: null,
        status: null,
        fuelType: null,
        transmission: null,
        capacityMin: null,
        capacityMax: null,
        mileageMin: null,
        mileageMax: null,
        registrationDate: null,
        insuranceExpiry: null,
        technicalInspectionExpiry: null,
        inUseInRoadmap: null,
        maintenanceDue: null,
        driverName: '',
      }
      this.getCars()
    },

    // Delete car
    confirmDelete(id) {
      this.carToDelete = id
      this.$refs.confirmDeleteModal.show()
    },

    cancelDelete() {
      this.carToDelete = null
    },

    async handleDeleteConfirmed() {
      if (!this.carToDelete) return

      try {
        this.isLoading = true
        await axios.delete(`/api/cars/delete/${this.carToDelete}/`)
        this.getCars()
        this.showToast('success', 'top-center', 'Voiture supprimée avec succès')
      } catch (error) {
        console.error('Error deleting car:', error)
        this.showToast('danger', 'top-center', 'Erreur lors de la suppression de la voiture')
      } finally {
        this.isLoading = false
        this.carToDelete = null
      }
    },

    // Status toggle methods
    confirmToggleStatus(id, makeActive) {
      this.carToToggle = id
      this.newStatus = makeActive

      this.statusModalTitle = makeActive ? 'Activer la voiture' : 'Désactiver la voiture'
      this.statusModalMessage = makeActive
        ? 'Êtes-vous sûr de vouloir activer cette voiture?'
        : 'Êtes-vous sûr de vouloir désactiver cette voiture?'

      this.$refs.confirmStatusModal.show()
    },

    cancelStatusChange() {
      this.carToToggle = null
      this.newStatus = null
    },

    handleStatusConfirmed() {
      if (this.carToToggle !== null) {
        this.toggleStatus(this.carToToggle, this.newStatus)
      }
    },

    async toggleStatus(id, makeActive) {
      try {
        this.toggleStatusLoading = id
        await axios.post(`/api/cars/toggle-status/${id}/`, { is_active: makeActive })

        // Update the item in the cars array to avoid a full reload
        const index = this.cars.findIndex(car => car.id === id)
        if (index !== -1) {
          this.cars[index].is_active = makeActive
        }

        this.showToast(
          'success',
          'top-center',
          `La voiture a été ${makeActive ? 'activée' : 'désactivée'} avec succès`,
        )
      } catch (error) {
        console.error('Error toggling car status:', error)
        this.showToast('danger', 'top-center', 'Erreur lors du changement de statut')
      } finally {
        this.toggleStatusLoading = null
        this.carToToggle = null
        this.newStatus = null
      }
    },

    // Bulk actions
    onSelectedRowsChange(params) {
      this.selectedRows = params.selectedRows
    },

    clearSelection() {
      this.$refs.carsTable.unselectAllRows()
      this.selectedRows = []
    },

    bulkActivate() {
      this.bulkModalTitle = 'Activer les voitures sélectionnées'
      this.bulkModalMessage = 'Êtes-vous sûr de vouloir activer toutes les voitures sélectionnées?'
      this.bulkAction = 'activate'
      this.$refs.confirmBulkModal.show()
    },

    bulkDeactivate() {
      this.bulkModalTitle = 'Désactiver les voitures sélectionnées'
      this.bulkModalMessage = 'Êtes-vous sûr de vouloir désactiver toutes les voitures sélectionnées?'
      this.bulkAction = 'deactivate'
      this.$refs.confirmBulkModal.show()
    },

    cancelBulkChange() {
      this.bulkAction = null
    },

    async handleBulkConfirmed() {
      if (!this.bulkAction) return

      const isActivate = this.bulkAction === 'activate'
      const ids = this.selectedRows.map(row => row.id)

      try {
        this.isLoading = true
        await axios.post('/api/cars/bulk-toggle-status/', {
          ids,
          is_active: isActivate,
        })

        // Update the status in the local data
        ids.forEach(id => {
          const index = this.cars.findIndex(car => car.id === id)
          if (index !== -1) {
            this.cars[index].is_active = isActivate
          }
        })

        this.clearSelection()
        this.showToast(
          'success',
          'top-center',
          `${ids.length} voitures ont été ${isActivate ? 'activées' : 'désactivées'} avec succès`,
        )
      } catch (error) {
        console.error('Error performing bulk action:', error)
        this.showToast('danger', 'top-center', 'Erreur lors de la mise à jour des statuts')
      } finally {
        this.isLoading = false
        this.bulkAction = null
      }
    },

    // Pagination methods
    onPageChange(params) {
      this.currentPage = params.currentPage
      this.getCars()
    },

    onPerPageChange(params) {
      this.pageLength = params.currentPerPage
      this.currentPage = 1
      this.getCars()
    },

    // Toast notification
    showToast(variant, position, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Notification',
          icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          text,
          variant,
        },
      }, {
        position,
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
@import "@core/scss/vue/libs/vue-select.scss";

// Enhanced table styles
.cars-table {
  .vgt-table {
    border-collapse: separate;
    border-spacing: 0;

    th {
      position: relative;
      font-weight: 600;

      &.active-sorting {
        background-color: rgba($color: #7367f0, $alpha: 0.1);
        color: #7367f0;
      }

      &.sorting-header {
        cursor: pointer;

        &:hover {
          background-color: rgba($color: #7367f0, $alpha: 0.05);
        }
      }
    }

    td {
      vertical-align: middle;
    }
  }

  // Status badges
  .badge-glow {
    &.bg-success {
      box-shadow: 0 0 10px rgba(40, 199, 111, 0.6);
    }

    &.bg-danger {
      box-shadow: 0 0 10px rgba(234, 84, 85, 0.6);
    }
  }
}

// Consistent form field sizing
.form-control, .vs__dropdown-toggle {
  height: 40px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  transition: all 0.3s ease;

  &:focus, &:active, &:hover {
    border-color: #ea5455;
    box-shadow: none;
  }
}

// Improve v-select styling to match regular inputs
.select-search-option {
  .vs__dropdown-toggle {
    padding: 5px 0;
    min-height: 40px;
    display: flex;
    align-items: center;
  }

  .vs__selected-options {
    padding: 0 6px;
    align-items: center;
  }

  .vs__search {
    padding: 5px 8px;
    margin: 0;
  }

  .vs__actions {
    padding-top: 0;
    padding-bottom: 0;
  }

  .vs__clear {
    height: 22px;
  }

  .vs__open-indicator {
    height: 17px;
  }
}

// Badge styling to match the image
.badge-round {
  padding: 0.3rem 0.65rem;
  font-size: 0.9rem;
  font-weight: 500;
  background-color: #ea5455 !important;
}

// Add styles for statistic cards
.card {
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);

  .card-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #5e5873;
  }

  .text-center {
    min-height: 120px;
  }

  h2 {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }

  &.bg-light-primary {
    background-color: rgba($color: #7367f0, $alpha: 0.12) !important;
  }

  &.bg-light-success {
    background-color: rgba($color: #28c76f, $alpha: 0.12) !important;
  }

  &.bg-light-warning {
    background-color: rgba($color: #ff9f43, $alpha: 0.12) !important;
  }

  &.bg-light-info {
    background-color: rgba($color: #00cfe8, $alpha: 0.12) !important;
  }
}

// Empty state styling
.empty-state {
  padding: 3rem 1rem;

  svg {
    opacity: 0.6;
  }
}

// Animations
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

// Button styling
.btn-danger {
  background-color: #ea5455 !important;
  border-color: #ea5455 !important;
}
</style>
/* Align action icons in the action column */
.action-icons-gap > * {
  margin-right: 0.25rem;
}
.action-icons-gap > *:last-child {
  margin-right: 0;
}
